import type { SliceDatum } from '../model/chart-data.js';

/**
 * Talabnomalar holati bo'yicha taqsimot — halqa diagramma.
 *
 * Halqa (to'liq doira emas) ataylab: o'rtasidagi bo'shliqqa JAMI son
 * yoziladi va odam avval "qancha", keyin "qanday taqsimlangan" degan
 * savolga javob oladi.
 */

const SIZE = 200;
const R = 74;
const STROKE = 26;
const CIRCUMFERENCE = 2 * Math.PI * R;

export interface StatusDonutProps {
  slices: readonly SliceDatum[];
  /** Halqa o'rtasidagi izoh — masalan "talabnoma". */
  totalLabel: string;
  labelOf: (key: string) => string;
}

export const StatusDonut = ({ slices, totalLabel, labelOf }: StatusDonutProps) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  let offset = 0;

  return (
    /* `h-full` — yonidagi kartochka baland bo'lsa halqa pastda emas,
       O'RTADA tursin: ikki kartochka bir qatorda va bo'sh joy ko'zga
       tashlanadi. */
    <div className="flex h-full flex-col items-center justify-center gap-4 sm:flex-row">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="size-44 shrink-0"
        role="img"
        aria-label={totalLabel}
      >
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {slices.map((slice) => {
            const length = total === 0 ? 0 : (slice.value / total) * CIRCUMFERENCE;
            const dash = `${length} ${CIRCUMFERENCE - length}`;
            const element = (
              <circle
                key={slice.key}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={R}
                fill="none"
                stroke={slice.color}
                strokeWidth={STROKE}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return element;
          })}
        </g>

        <text
          x={SIZE / 2}
          y={SIZE / 2 - 2}
          textAnchor="middle"
          fontSize="30"
          fontWeight="600"
          fill="var(--color-text)"
        >
          {total}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 18}
          textAnchor="middle"
          fontSize="12"
          fill="var(--color-text-faint)"
        >
          {totalLabel}
        </text>
      </svg>

      <ul className="grid min-w-0 flex-1 gap-1.5 text-sm sm:grid-cols-2">
        {slices.map((slice) => (
          <li key={slice.key} className="flex min-w-0 items-center gap-2">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ background: slice.color }}
            />
            <span className="min-w-0 flex-1 truncate text-text-muted">{labelOf(slice.key)}</span>
            <span className="tabular text-text">{slice.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
